import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { Popover } from "@headlessui/react";
import { GoogleMap, useLoadScript, Marker, Circle } from "@react-google-maps/api";
import { cn } from "../../utils";
import MapSearchBar from "./MapSearchBar";
import MobileSearch from "./MobileSearch";
import useAssessments from "../../hooks/use-assessments";
import useClinics from "./use-clinics";
import mapStyles from "./map-styles";
import {
  MARKER_URL,
  COMING_SOON_MARKER_URL,
  SELECTED_MARKER_URL,
  CIRCLE_CENTRE_MARKER,
} from "../../constants";

const getSearchDataFromUri = () => {
  const params = new URLSearchParams(window.location.search);

  const lat = params.get("lat");
  const lng = params.get("lng");
  const search = params.get("search");

  return {
    lat: lat ? Number(lat) : null,
    lng: lng ? Number(lng) : null,
    search: search ? decodeURIComponent(search) : "",
  };
};

const getLabel = (assessments, filters) => {
  try {
    if (!filters || !assessments || assessments.length === 0 || filters.length === 0) {
      return "All Assessments";
    }

    const matchingAssessments = assessments
      .filter((assessment) => filters.includes(assessment.id))
      .map((assessment) => assessment.title.rendered);

    return matchingAssessments.join(", ");
  } catch {
    return "All Assessments";
  }
};

export default function FindClinic() {
  const mapRef = useRef();
  const circleRef = useRef(null);
  const [selected, setSelected] = useState(null);
  const [mobileMode, setMobileMode] = useState("controls");
  const [searchData, setSearchData] = useState(() => getSearchDataFromUri());
  const [filters, setFilters] = useState([]);
  const [newFilters, setNewFilters] = useState(filters);

  const { data: assessments } = useAssessments();
  const { data: clinics, isLoading: clinicsLoading } = useClinics({
    lat: searchData?.lat,
    lng: searchData?.lng,
    assessments: filters.join(","),
    onSuccess: (data) => boundMap(data),
  });

  const assessmentsLabel = getLabel(assessments, filters);

  /**
   * 1. Sort **all** clinics by distance (ascending).
   * 2. Use this unfiltered list in the <ul> below.
   */
  const sortedClinics = useMemo(() => {
    if (!clinics) return [];
    return clinics.slice().sort((a, b) => a.distance - b.distance);
  }, [clinics]);

  /**
   * For **map markers**, show only those within 100 miles (distance <= 100).
   */
  const filteredClinics = useMemo(() => {
    return sortedClinics.filter((clinic) => Number(clinic.distance) <= 100);
  }, [sortedClinics]);

  // Rebound the map if mobile mode changes to "map" or if clinics change
  useEffect(() => {
    if (mobileMode === "map") {
      boundMap(filteredClinics);
    }
  }, [mobileMode, filteredClinics]);

  // Update map bounds to show the filtered clinics
  const boundMap = (clinicsToBound) => {
    if (!mapRef.current) return;

    const bounds = new google.maps.LatLngBounds();

    // If no clinics but we have lat/lng, just center on that lat/lng
    if (clinicsToBound?.length === 0 && searchData.lat && searchData.lng) {
      bounds.extend({ lat: searchData.lat, lng: searchData.lng });
      mapRef.current.fitBounds(bounds);
      mapRef.current.setZoom(16);
    }

    // If we have filtered clinics, extend bounds to show them
    if (clinicsToBound?.length > 0) {
      clinicsToBound.forEach((clinic) =>
        bounds.extend({
          lat: Number(clinic.lat),
          lng: Number(clinic.lng),
        })
      );
      mapRef.current.fitBounds(bounds);
    }

    // Also fit circle if present
    if (circleRef.current) {
      mapRef.current.fitBounds(circleRef.current.getBounds());
    } else {
      mapRef.current.fitBounds(bounds);
    }
  };

  const { isLoaded: isMapLoaded } = useLoadScript({
    googleMapsApiKey: "AIzaSyDa_dMs_T_s9eBnhzndbts4cm5us5b3O-c",
  });

  // Initialize the map
  const onLoad = useCallback(
    (map) => {
      mapRef.current = map;
      boundMap(filteredClinics);
    },
    [filteredClinics]
  );

  // Map configuration options
  const options = useMemo(
    () => ({
      disableDefaultUI: true,
      clickableIcons: false,
      maxZoom: 16,
      styles: mapStyles,
    }),
    []
  );

  /**
   * Each clinic card now has its own active tab, stored in an
   * object keyed by clinic ID. Example: { [clinicId]: "tab-contact" }
   */
  const [activeTabs, setActiveTabs] = useState({});

  // When a tab is clicked, update only that clinic’s tab state
  const handleTabClick = (clinicId, tabId) => {
    setActiveTabs((prev) => ({
      ...prev,
      [clinicId]: tabId,
    }));
  };

  // A helper to see if a given clinic’s active tab equals a given tab ID
  const isTabActive = (clinicId, tabId) =>
    activeTabs[clinicId] === tabId;

  // For a "default" tab, you can decide if no tab is set => "tab-assessments"
  // (like a fallback). We can do that on the hidden panels below.

  return (
    <div className="search-map">
      {/* Controls / List Section */}
      <div
        className={cn(
          "search-map__controls",
          mobileMode === "controls" && "search-map__controls--active"
        )}
      >
        <MobileSearch
          currentMode={mobileMode}
          changeMode={() => setMobileMode("map")}
          defaultSearchTerm={searchData.search}
          assessments={assessments}
          setFilters={setFilters}
          filters={filters}
          onCoordinatesChange={({ lat, lng }) =>
            setSearchData({ ...searchData, lat, lng })
          }
        />
        <div className="search-map__clinics">
          <div className="search-map__desktop-search">
            <MapSearchBar
              defaultSearchTerm={searchData.search}
              onCoordinatesChange={({ lat, lng }) =>
                setSearchData({ ...searchData, lat, lng })
              }
              className="search-map__clinics-inner-bar"
            />
            <Popover className="search-map__desktop-filters">
              <Popover.Button dangerouslySetInnerHTML={{ __html: assessmentsLabel }} />
              <Popover.Panel>
                {assessments?.map((assessment) => (
                  <label key={assessment.id}>
                    <input
                      type="checkbox"
                      value={assessment.id}
                      checked={newFilters.includes(assessment.id)}
                      onChange={() =>
                        newFilters.includes(assessment.id)
                          ? setNewFilters(
                              newFilters.filter((f) => f !== assessment.id)
                            )
                          : setNewFilters([...newFilters, assessment.id])
                      }
                    />
                    <span
                      dangerouslySetInnerHTML={{
                        __html: assessment.title?.rendered,
                      }}
                    />
                  </label>
                ))}
                <Popover.Button
                  className="search-map__desktop-filters-apply"
                  onClick={() => setFilters(newFilters)}
                >
                  Apply
                </Popover.Button>
              </Popover.Panel>
            </Popover>
          </div>

          {clinicsLoading && <p>Loading...</p>}

          {/*
              Here we display ALL clinics in the list (sorted by distance).
              We do NOT filter by 100 miles.
          */}
          {sortedClinics?.length === 0 && !clinicsLoading && (
            <p>No clinics found</p>
          )}
          {sortedClinics?.length > 0 && !clinicsLoading && (
            <ul className="search-map__clinics-list">
              {sortedClinics.map((clinic) => (
                <li className={`search-map__clinics-list-item clinic-card${clinic.comingSoon ? ' clinic-card--coming-soon' : ''}${clinic.comingSoon && !Number(clinic.distance) > 0 ? ' clinic-card--no-distance' : ''}`} key={clinic.id}>
                  {clinic.comingSoon && <div className="clinic-card__coming-soon">
                    Coming soon
                </div> }
                <a href={clinic.permalink} className="clinic-card__link">
                        {Number(clinic.distance) > 0 && (
                            <span className={`clinic-card__distance${clinic.comingSoon ? ' clinic-card__distance--coming-soon' : ''}`}>
                            {Number(clinic.distance).toFixed(2)} miles away
                            </span>
                        )}
                    <p className="clinic-card__title">
                      {clinic.mapTitle !== null && clinic.mapTitle !== ""
                        ? clinic.mapTitle
                        : clinic.title}
                    </p>
                    <span className="clinic-card__location">
                      {clinic.location}
                    </span>
                  </a>

                  {/* Tabs */}
                  <div
                    className="clinic-card__info"
                    role="tablist"
                    aria-label="Clinic card tabs"
                  >
                    <button
                      className="clinic-card__tab"
                      role="tab"
                      id={`${clinic.id}-tab-assessments`}
                      aria-controls={`${clinic.id}-panel-assessments`}
                      aria-selected={isTabActive(clinic.id, "tab-assessments")}
                      onClick={() => handleTabClick(clinic.id, "tab-assessments")}
                    >
                      Assessments
                    </button>
                    <button
                      className="clinic-card__tab"
                      role="tab"
                      id={`${clinic.id}-tab-contact`}
                      aria-controls={`${clinic.id}-panel-contact`}
                      aria-selected={isTabActive(clinic.id, "tab-contact")}
                      onClick={() => handleTabClick(clinic.id, "tab-contact")}
                    >
                      Contact
                    </button>
                    {(clinic?.monday || 
                    clinic?.tuesday || 
                    clinic?.wednesday || 
                    clinic?.thursday || 
                    clinic?.friday || 
                    clinic?.saturday || 
                    clinic?.sunday) && (
                    <button
                      className="clinic-card__tab"
                      role="tab"
                      id={`${clinic.id}-tab-opening-hours`}
                      aria-controls={`${clinic.id}-panel-opening-hours`}
                      aria-selected={isTabActive(clinic.id, "tab-opening-hours")}
                      onClick={() => handleTabClick(clinic.id, "tab-opening-hours")}
                    >
                      Opening Hours
                    </button>
                    )}
                  </div>

                  {/* Tab Panels */}
                  <div
                    className="clinic-card__panel"
                    id={`${clinic.id}-panel-assessments`}
                    role="tabpanel"
                    aria-labelledby={`${clinic.id}-tab-assessments`}
                    hidden={
                      // If this clinic’s activeTab is NOT "tab-assessments",
                      // we hide it. Otherwise, show it.
                      activeTabs[clinic.id] !== "tab-assessments"
                    }
                  >
                    <ul className="clinic-card__assessments"
                      dangerouslySetInnerHTML={{
                        __html: clinic.assessments,
                      }}
                    />                    
                  </div>
                  <div
                    className="clinic-card__panel"
                    id={`${clinic.id}-panel-contact`}
                    role="tabpanel"
                    aria-labelledby={`${clinic.id}-tab-contact`}
                    hidden={activeTabs[clinic.id] !== "tab-contact"}
                  >
                    <p className="clinic-card__email"
                      dangerouslySetInnerHTML={{
                        __html: clinic.email,
                      }}
                    />
                    <p className="clinic-card__phone"
                      dangerouslySetInnerHTML={{
                        __html: clinic.phone,
                      }}
                    />
                    <p
                      className="clinic-card__address"
                      dangerouslySetInnerHTML={{
                        __html: clinic.address,
                      }}
                    />
                  </div>
                    
                  {(clinic?.monday || 
                    clinic?.tuesday || 
                    clinic?.wednesday || 
                    clinic?.thursday || 
                    clinic?.friday || 
                    clinic?.saturday || 
                    clinic?.sunday) && (
                  <div
                    className="clinic-card__panel"
                    id={`${clinic.id}-panel-opening-hours`}
                    role="tabpanel"
                    aria-labelledby={`${clinic.id}-tab-opening-hours`}
                    hidden={activeTabs[clinic.id] !== "tab-opening-hours"}
                  >
                    {clinic?.monday &&
                      <div className="clinic-card__opening">
                      <p className="clinic-card__day">Monday</p>
                      <p className="clinic-card__time"
                        dangerouslySetInnerHTML={{
                          __html: clinic.monday,
                        }}
                      />
                      </div>
                    }
                    {clinic?.tuesday &&
                      <div className="clinic-card__opening">
                      <p className="clinic-card__day">Tuesday</p>
                      <p className="clinic-card__time"
                        dangerouslySetInnerHTML={{
                          __html: clinic.tuesday,
                        }}
                      />
                      </div>
                    } 
                    {clinic?.wednesday &&
                    <div className="clinic-card__opening">
                    <p className="clinic-card__day">Wednesday</p>
                    <p className="clinic-card__time"
                      dangerouslySetInnerHTML={{
                        __html: clinic.wednesday,
                      }}
                    />
                    </div>
                    }
                    {clinic?.thursday &&
                    <div className="clinic-card__opening">
                    <p className="clinic-card__day">Thursday</p>
                    <p className="clinic-card__time"
                      dangerouslySetInnerHTML={{
                        __html: clinic.thursday,
                      }}
                    />
                    </div>
                    }
                    {clinic?.friday &&
                    <div className="clinic-card__opening">
                    <p className="clinic-card__day">Friday</p>
                    <p className="clinic-card__time"
                      dangerouslySetInnerHTML={{
                        __html: clinic.friday,
                      }}
                    />
                    </div>
                    }
                    {clinic?.saturday &&
                      <div className="clinic-card__opening">
                      <p className="clinic-card__day">Saturday</p>
                      <p className="clinic-card__time"
                        dangerouslySetInnerHTML={{
                          __html: clinic.saturday,
                        }}
                      />
                      </div>
                    }
                    {clinic?.sunday &&
                      <div className="clinic-card__opening">
                      <p className="clinic-card__day">Sunday</p>
                      <p className="clinic-card__time"
                        dangerouslySetInnerHTML={{
                          __html: clinic.sunday,
                        }}
                      />
                      </div>
                    }
                  </div>
                  
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Map Section */}
      <div
        className={cn(
          "search-map__map",
          mobileMode === "map" && "search-map__map--active"
        )}
      >
        <MobileSearch
          currentMode={mobileMode}
          changeMode={() => setMobileMode("controls")}
          assessments={assessments}
          setFilters={setFilters}
          filters={filters}
          defaultSearchTerm={searchData.search}
          onCoordinatesChange={({ lat, lng }) =>
            setSearchData({ ...searchData, lat, lng })
          }
        />

        {isMapLoaded && (
          <GoogleMap
            mapContainerClassName="search-map__map-container"
            onLoad={onLoad}
            options={options}
            zoom={16}
          >
            {/*
              Only show markers for clinics within 100 miles (filteredClinics)
            */}
            {filteredClinics.map((clinic) => (
              <Marker
                key={clinic.id}
                icon={clinic.id === selected?.id ? SELECTED_MARKER_URL : clinic.comingSoon ? COMING_SOON_MARKER_URL : MARKER_URL}
                onClick={() =>
                  setSelected(clinic.id === selected?.id ? null : clinic)
                }
                animation={
                  clinic.id === selected?.id
                    ? window.google.maps.Animation.BOUNCE
                    : null
                }
                position={{
                  lat: Number(clinic.lat),
                  lng: Number(clinic.lng),
                }}
              />
            ))}

            {/* 100-mile circle */}
            {searchData.lat && searchData.lng && (
              <>
                <Circle
                  onLoad={(circle) => (circleRef.current = circle)}
                  center={{
                    lat: Number(searchData.lat),
                    lng: Number(searchData.lng),
                  }}
                  radius={160934} // ~100 miles in meters
                  options={{
                    fillOpacity: 0.2,
                    fillColor: "#7B3CEA",
                    strokeWeight: 1,
                    strokeColor: "#7B3CEA",
                  }}
                />
                <Marker
                  position={{
                    lat: Number(searchData.lat),
                    lng: Number(searchData.lng),
                  }}
                  icon={CIRCLE_CENTRE_MARKER}
                />
              </>
            )}
          </GoogleMap>
        )}

        {selected && (
          <div className="search-map__selected clinic-card">
            <a href={selected.permalink} className="clinic-card__link">
              {Number(selected.distance) > 0 && (
                <span className="clinic-card__distance">
                  {Number(selected.distance).toFixed(2)} miles away
                </span>
              )}
              <p className="clinic-card__title">
                {selected.mapTitle !== null && selected.mapTitle !== ""
                  ? selected.mapTitle
                  : selected.title}
              </p>
              <span className="clinic-card__location">{selected.location}</span>
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
